// Reader preferences: localStorage for everyone, mirrored to the account when
// signed in. Theme is applied to <html data-theme> so CSS tokens switch.
import { DEFAULT_READER_SETTINGS, readerSettings, type ReaderSettings } from "@bibleunleashed/shared";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, put } from "./api";
import { useUser } from "./auth";

const LS_KEY = "bu.readerSettings";

function load(): ReaderSettings {
  try {
    const parsed = readerSettings.safeParse(JSON.parse(localStorage.getItem(LS_KEY) ?? "{}"));
    return parsed.success ? parsed.data : DEFAULT_READER_SETTINGS;
  } catch {
    return DEFAULT_READER_SETTINGS;
  }
}

function applyTheme(theme: ReaderSettings["theme"]): void {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

interface Ctx {
  settings: ReaderSettings;
  update: (patch: Partial<ReaderSettings>) => void;
}

const SettingsContext = createContext<Ctx | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<ReaderSettings>(load);
  const { data: user } = useUser();
  const syncedFor = useRef<string | null>(null);

  useEffect(() => applyTheme(settings.theme), [settings.theme]);

  // On sign-in, the account's saved settings win once; afterwards edits flow up.
  useEffect(() => {
    if (!user || syncedFor.current === user.id) return;
    syncedFor.current = user.id;
    api<ReaderSettings>("/api/settings/reader")
      .then((remote) => {
        setSettings(remote);
        localStorage.setItem(LS_KEY, JSON.stringify(remote));
      })
      .catch(() => {});
  }, [user]);

  const update = useCallback(
    (patch: Partial<ReaderSettings>) => {
      setSettings((prev) => {
        const next = { ...prev, ...patch };
        localStorage.setItem(LS_KEY, JSON.stringify(next));
        if (user) void put("/api/settings/reader", next).catch(() => {});
        return next;
      });
    },
    [user],
  );

  const value = useMemo(() => ({ settings, update }), [settings, update]);
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): Ctx {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings outside SettingsProvider");
  return ctx;
}
